import {FC, useEffect, useMemo, useState} from 'react'
import {Form, Modal} from 'react-bootstrap'
import {useQuery, useQueryClient} from 'react-query'
import {useSearchParams} from 'react-router-dom'
import {getApiErrorMessage, QUERIES} from '../../../../../_metronic/helpers'
import {Toast} from '../../../../../_metronic/partials/widgets'
import {
  SPREADSHEET_IMPORT_FIELDS,
  SPREADSHEET_IMPORT_QUERY_PARAM,
  SpreadsheetColumnMapping,
  SpreadsheetImportField,
  SpreadsheetTaskImport,
} from './_models'
import {confirmSpreadsheetTaskImport, getSpreadsheetTaskImport} from './_requests'

const UNIQUE_FIELDS: SpreadsheetImportField[] = [
  'name',
  'description',
  'status',
  'priority',
  'begin_date',
  'expected_date',
  'end_date',
  'tags',
]

type Props = {
  importId?: number | null
  onImported?: () => void
}

/** Rótulos de pipeline (ETAPA 1…), não andamento conversível. */
function valueLooksLikePipelineLabel(value: string): boolean {
  const normalized = value.toLowerCase().trim()
  if (!normalized) return false
  if (valueLooksLikeConvertibleStatus(normalized)) return false
  return (
    /^(etapa|fase|stage|phase|pipeline)\s*[\d_\-#]+$/u.test(normalized) ||
    /^(etapa|fase|stage|phase)\s+[a-z]$/u.test(normalized) ||
    /^(etapa|fase|stage|phase)\s+\d+/u.test(normalized)
  )
}

function valueLooksLikeConvertibleStatus(value: string): boolean {
  const normalized = value.toLowerCase().trim()
  if (!normalized) return false
  if (
    ['entregue', 'done', 'complete', 'completed', 'closed', 'concluido', 'concluído', 'finalizado'].includes(
      normalized
    ) ||
    normalized.includes('conclu') ||
    normalized.includes('done')
  ) {
    return true
  }
  if (
    ['em andamento', 'em progresso', 'in progress', 'progress', 'progresso', 'doing', 'working', 'started', 'active', 'wip'].includes(
      normalized
    ) ||
    normalized.includes('andamento') ||
    normalized.includes('progress') ||
    normalized.includes('execuc')
  ) {
    return true
  }
  if (
    ['backlog', 'to do', 'todo', 'pending', 'open', 'new', 'fila', 'a fazer', 'aguardando'].includes(normalized) ||
    normalized.includes('backlog') ||
    normalized.includes('to do') ||
    normalized.includes('a fazer')
  ) {
    return true
  }
  return false
}

/** Sempre um dos 3 status TaskRush — nunca o texto cru da planilha. */
function normalizePreviewStatus(raw: string): string {
  const normalized = raw.toLowerCase().trim()
  if (!normalized || valueLooksLikePipelineLabel(raw)) {
    return 'Backlog'
  }
  if (normalized.includes('conclu') || normalized.includes('done') || normalized === 'entregue' || normalized === 'finalizado') {
    return 'Entregue'
  }
  if (
    normalized.includes('andamento') ||
    normalized.includes('progress') ||
    normalized.includes('execuc') ||
    ['doing', 'working', 'started', 'active', 'wip'].includes(normalized)
  ) {
    return 'Em andamento'
  }
  if (
    normalized.includes('backlog') ||
    normalized.includes('to do') ||
    normalized.includes('a fazer') ||
    ['todo', 'pending', 'open', 'new', 'fila', 'aguardando'].includes(normalized)
  ) {
    return 'Backlog'
  }
  return 'Backlog'
}

function formatDateBr(raw: string): string {
  const value = raw.trim()
  if (!value) {
    return raw
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/)
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
  }

  return raw
}

function samplesSupportStatus(column: string, sampleRows: Array<Record<string, string | null>>): boolean {
  const samples = sampleRows.map((row) => String(row[column] ?? '').trim()).filter(Boolean)
  if (samples.length === 0) {
    return true
  }
  if (samples.some((value) => valueLooksLikePipelineLabel(value))) {
    return false
  }
  const recognized = samples.filter((value) => valueLooksLikeConvertibleStatus(value)).length
  return recognized >= Math.ceil(samples.length / 2)
}

function applyFieldChange(
  mapping: SpreadsheetColumnMapping[],
  column: string,
  field: SpreadsheetImportField,
  sampleRows: Array<Record<string, string | null>> = []
): SpreadsheetColumnMapping[] {
  let nextField = field
  if (nextField === 'status' && !samplesSupportStatus(column, sampleRows)) {
    nextField = 'ignore'
  }

  return mapping.map((item) => {
    if (item.column === column) {
      return {...item, field: nextField}
    }
    if (UNIQUE_FIELDS.includes(nextField) && item.field === nextField) {
      return {...item, field: 'ignore'}
    }
    return item
  })
}

const SpreadsheetImportMappingModal: FC<Props> = ({importId: importIdProp, onImported}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryImportId = searchParams.get(SPREADSHEET_IMPORT_QUERY_PARAM)
  const importId = importIdProp ?? (queryImportId ? Number(queryImportId) : null)
  const queryClient = useQueryClient()
  const [mapping, setMapping] = useState<SpreadsheetColumnMapping[]>([])
  const [isConfirming, setIsConfirming] = useState(false)

  const enabled = !!importId && importId > 0

  const {data, isLoading, isError} = useQuery(
    ['spreadsheetTaskImport', importId],
    () => getSpreadsheetTaskImport(importId as number),
    {
      enabled,
      refetchInterval: (current) => {
        if (!current) {
          return 3000
        }
        if (['queued', 'processing', 'importing'].includes(current.status)) {
          return current.poll_interval_ms || 3000
        }
        return false
      },
    }
  )

  useEffect(() => {
    if (!data?.mapping?.length) {
      return
    }
    const sampleRows = data.sample_rows || []
    const sanitized = data.mapping.map((item) => {
      if (item.field === 'status' && !samplesSupportStatus(item.column, sampleRows)) {
        return {...item, field: 'ignore' as SpreadsheetImportField}
      }
      return item
    })
    setMapping(sanitized)
  }, [data?.id, data?.status, data?.mapping, data?.sample_rows])

  const previewRows = useMemo(() => {
    if (!data?.sample_rows?.length) {
      return []
    }
    const fieldByColumn = Object.fromEntries(mapping.map((item) => [item.column, item.field]))
    return data.sample_rows.slice(0, 5).map((row) => {
      const preview: Record<string, string> = {}
      Object.entries(fieldByColumn).forEach(([column, field]) => {
        if (field === 'ignore') {
          return
        }
        const raw = row[column] ?? ''
        preview[field] =
          field === 'status'
            ? normalizePreviewStatus(String(raw))
            : field === 'begin_date' || field === 'expected_date' || field === 'end_date'
              ? formatDateBr(String(raw))
              : String(raw)
      })
      return preview
    })
  }, [data?.sample_rows, mapping])

  useEffect(() => {
    if (data?.status !== 'completed') {
      return
    }
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey?.[0] === 'string' &&
        String(query.queryKey[0]).startsWith(QUERIES.TASKS_LIST),
    })
    onImported?.()
  }, [data?.status, onImported, queryClient])

  const handleClose = () => {
    if (isConfirming) {
      return
    }
    if (queryImportId) {
      const next = new URLSearchParams(searchParams)
      next.delete(SPREADSHEET_IMPORT_QUERY_PARAM)
      setSearchParams(next)
    }
    setMapping([])
  }

  const hasName = mapping.some((item) => item.field === 'name')

  const handleConfirm = async () => {
    if (!importId || !hasName) {
      Toast.fire({icon: 'warning', title: 'Mapeie uma coluna para o nome da tarefa.'})
      return
    }

    setIsConfirming(true)
    try {
      await confirmSpreadsheetTaskImport(importId, mapping)
      Toast.fire({
        icon: 'success',
        title: 'Importação iniciada',
        text: 'As tarefas serão criadas em segundo plano. Você pode continuar usando o sistema.',
      })
      await queryClient.invalidateQueries(['spreadsheetTaskImport', importId])
    } catch (error) {
      Toast.fire({icon: 'error', title: getApiErrorMessage(error)})
    } finally {
      setIsConfirming(false)
    }
  }

  const renderBody = (importData?: SpreadsheetTaskImport) => {
    if (isLoading && !importData) {
      return (
        <div className='text-center py-10'>
          <div className='spinner-border text-primary mb-4' />
          <p className='text-muted mb-0'>Carregando importação...</p>
        </div>
      )
    }

    if (isError || !importData) {
      return <p className='text-muted'>Não foi possível carregar esta importação.</p>
    }

    if (['queued', 'processing'].includes(importData.status)) {
      return (
        <div className='text-center py-8'>
          <div className='spinner-border text-primary mb-4' />
          <p className='fw-semibold mb-1'>RushMind analisando a planilha</p>
          <p className='text-muted mb-0'>
            Você pode fechar esta janela e continuar usando o sistema. Enviaremos uma notificação
            quando o de-para estiver pronto.
          </p>
        </div>
      )
    }

    if (importData.status === 'importing') {
      return (
        <div className='text-center py-8'>
          <div className='spinner-border text-primary mb-4' />
          <p className='fw-semibold mb-1'>Criando tarefas em segundo plano</p>
          <p className='text-muted mb-0'>
            A importação está em andamento. Você pode continuar usando o sistema.
          </p>
        </div>
      )
    }

    if (importData.status === 'completed') {
      return (
        <div className='py-6'>
          <p className='fw-semibold'>
            Importação concluída: {importData.created_count} tarefa
            {importData.created_count === 1 ? '' : 's'} criada
            {importData.created_count === 1 ? '' : 's'}.
          </p>
          {importData.skipped_count > 0 && (
            <p className='text-muted'>{importData.skipped_count} linha(s) ignorada(s).</p>
          )}
        </div>
      )
    }

    if (importData.status === 'failed') {
      return (
        <div className='py-6'>
          <p className='text-danger mb-0'>
            {importData.error || 'Não foi possível concluir a importação da planilha.'}
          </p>
        </div>
      )
    }

    return (
      <>
        <p className='text-muted mb-4 fs-6'>
          Confira o de-para sugerido pelo RushMind e ajuste o que for necessário. Apenas depois da
          confirmação as tarefas serão criadas no projeto.
          <br />
          <span className='fs-7'>
            Status no TaskRush só pode ser Backlog, Em andamento ou Entregue — o valor da planilha é
            convertido (ex.: &quot;em progresso&quot; → Em andamento), nunca copiado literalmente.
            Prazo = data prevista de entrega. Conclusão = quando a tarefa já foi entregue.
          </span>
        </p>
        <div className='table-responsive mb-6'>
          <table className='table table-row-dashed align-middle'>
            <thead>
              <tr className='text-muted fw-bold fs-7'>
                <th>Coluna da planilha</th>
                <th>Amostra</th>
                <th>Campo no TaskRush</th>
              </tr>
            </thead>
            <tbody>
              {mapping.map((item) => {
                const sample = data?.sample_rows?.[0]?.[item.column] ?? ''
                const sampleText = String(sample || '')
                const isPipelineSample = !!sampleText && valueLooksLikePipelineLabel(sampleText)
                const blocksStatus = !samplesSupportStatus(item.column, data?.sample_rows || [])
                return (
                  <tr key={item.column}>
                    <td className='fw-semibold'>{item.column}</td>
                    <td className='text-muted text-truncate' style={{maxWidth: 220}}>
                      {formatDateBr(sampleText) || '—'}
                      {isPipelineSample && (
                        <div className='fs-8 text-warning mt-1'>
                          Rótulo de pipeline — não conversível para status TaskRush
                        </div>
                      )}
                    </td>
                    <td>
                      <Form.Select
                        size='sm'
                        value={item.field}
                        onChange={(event) =>
                          setMapping(
                            applyFieldChange(
                              mapping,
                              item.column,
                              event.target.value as SpreadsheetImportField,
                              data?.sample_rows || []
                            )
                          )
                        }
                      >
                        {SPREADSHEET_IMPORT_FIELDS.map((field) => (
                          <option
                            key={field.value}
                            value={field.value}
                            disabled={field.value === 'status' && blocksStatus}
                          >
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {previewRows.length > 0 && (
          <div className='mb-4'>
            <h6 className='mb-3'>Prévia das primeiras linhas</h6>
            <div className='table-responsive'>
              <table className='table table-sm table-row-bordered'>
                <thead>
                  <tr className='fw-bold fs-8 text-muted'>
                    <th>Nome</th>
                    <th>Descrição</th>
                    <th>Status</th>
                    <th>Prioridade</th>
                    <th>Início</th>
                    <th>Prazo</th>
                    <th>Conclusão</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.name || '—'}</td>
                      <td className='text-truncate' style={{maxWidth: 180}}>
                        {row.description || '—'}
                      </td>
                      <td>{row.status || 'Backlog'}</td>
                      <td>{row.priority || '—'}</td>
                      <td>{row.begin_date || '—'}</td>
                      <td>{row.expected_date || '—'}</td>
                      <td>{row.end_date || '—'}</td>
                      <td className='text-truncate' style={{maxWidth: 140}}>
                        {row.tags || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!hasName && (
          <div className='alert alert-warning'>Mapeie uma coluna para o nome da tarefa.</div>
        )}
      </>
    )
  }

  return (
    <Modal
      show={enabled}
      onHide={handleClose}
      backdrop='static'
      size='xl'
      dialogClassName='modal-dialog modal-dialog-centered'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          De-para da planilha
          {data?.original_filename ? ` — ${data.original_filename}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody(data)}</Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={handleClose} disabled={isConfirming}>
          {data?.status === 'mapping_ready' ? 'Agora não' : 'Fechar'}
        </button>
        {data?.status === 'mapping_ready' && (
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleConfirm}
            disabled={!hasName || isConfirming}
          >
            {isConfirming ? 'Confirmando...' : 'Confirmar e importar'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export {SpreadsheetImportMappingModal}
